/**
 * The right-hand panel during a guided run — the script, not the log.
 *
 * Same place, same entries, same results as StepLog: a completed instruction
 * shows exactly what the free-form log would have shown for that step, through
 * the same StepResult. What differs is that the steps are written in advance,
 * so the panel can also show where this is going.
 *
 * Upcoming steps are visible on purpose. A student following instructions with
 * no idea how many are left is following a corridor; one who can see the shape
 * of the activity is doing an experiment.
 *
 * See docs/FeatureDesigns/chemistry-lab-guided-target.md.
 */
import type { GuidedProcedure, Stoichiometry } from '@padcanvas/shared';
import type { LabStep } from '../labState';
import { answerUnit, isSelfAdvancing, judgeQuantity } from '../guidedRun';
import { useState } from 'react';
import { StepResult } from './StepLog';

interface GuidedPanelProps {
  experiment: GuidedProcedure;
  /** The step being worked on. Equal to steps.length once the run is done. */
  cursor: number;
  /** What to say about the last off-script attempt, if there was one. */
  correction?: string;
  /** The bench step each finished instruction produced, by instruction index.
   *  Absent for instructions that change no chemistry (running a delivery
   *  tube, watching). */
  results: Record<number, LabStep | undefined>;
  loading: boolean;
  error?: string;
  /** Carries out an `observe` step — the one kind the bench cannot. */
  onContinue: () => void;
  /** The working the current step's question is judged against — the bench's
   *  own, so a student who reads the notebook and types what it says can never
   *  be told they are wrong. */
  working?: Stoichiometry;
  onRestart: () => void;
  /** A teacher's own experiment: reopen its steps in the editor. */
  onEdit?: () => void;
  /** A teacher's own experiment: back to an empty free bench. */
  onLeave?: () => void;
}

/**
 * A step that asks for a number.
 *
 * Wrong answers do not close the step and are never counted: this bench
 * diagnoses, it does not grade. What a wrong answer gets is the same thing a
 * wrong action gets — a sentence saying what to look at, and the step still
 * open.
 */
function QuantityAsk({ step, working, onCorrect }: {
  step: GuidedProcedure['steps'][number];
  working?: Stoichiometry;
  onCorrect: () => void;
}): React.JSX.Element | null {
  const [typed, setTyped] = useState('');
  const [said, setSaid] = useState<string | undefined>();
  if (step.expect.kind !== 'answer-quantity') return null;

  function submit(e: React.FormEvent): void {
    e.preventDefault();
    const verdict = judgeQuantity(step, typed, working);
    if (verdict.ok) { setSaid(undefined); onCorrect(); return; }
    setSaid(verdict.message);
  }

  return (
    <form className="chemlab-guided-ask" onSubmit={submit}>
      <label className="chemlab-guided-ask-field">
        <input
          value={typed}
          onChange={e => setTyped(e.target.value)}
          placeholder="Your answer"
          aria-label={step.instruction}
          autoComplete="off"
        />
        <span className="chemlab-guided-ask-unit">{answerUnit(step.expect)}</span>
      </label>
      <button className="chemlab-btn" type="submit">Check</button>
      {said && <p className="chemlab-guided-ask-said">{said}</p>}
    </form>
  );
}

export function GuidedPanel({
  experiment, cursor, correction, results, loading, error, onContinue, working, onRestart, onEdit, onLeave,
}: GuidedPanelProps): React.JSX.Element {
  const finished = cursor >= experiment.steps.length;
  /*
   * The step that just ran, found by result rather than by counting back from
   * the cursor — which is also right when the run has finished and there is no
   * current step to count back from. Its detail opens on arrival: it is what
   * the student is waiting to read, and making them click "More" to see what
   * their own action did is asking them to ask for what they came for.
   */
  const lastResultIndex = experiment.steps.reduce((last, _s, i) => (results[i] ? i : last), -1);
  /** Which result is open, once the student has chosen one — until then it is
   *  the newest, the same rule the free-form log follows. '' is "none", which
   *  is what closing the open one leaves. */
  const [openId, setOpenId] = useState<string | undefined>(undefined);

  return (
    <div className="chemlab-guided">
      <div className="chemlab-guided-head">
        <p className="chemlab-guided-eyebrow">Guided experiment</p>
        <h2 className="chemlab-guided-title">{experiment.title}</h2>
        <p className="chemlab-guided-progress">
          {finished ? 'Finished' : `Step ${cursor + 1} of ${experiment.steps.length}`}
        </p>
        {(onEdit || onLeave) && (
          <div className="chemlab-guided-teacher">
            {onEdit && <button type="button" onClick={onEdit}>Edit steps</button>}
            {onLeave && <button type="button" onClick={onLeave}>Free bench</button>}
          </div>
        )}
      </div>

      <ol className="chemlab-guided-steps">
        {experiment.steps.map((step, i) => {
          const state = i < cursor ? 'done' : i === cursor ? 'current' : 'upcoming';
          const result = results[i];
          return (
            <li key={i} className={`chemlab-guided-step chemlab-guided-${state}`}>
              <span className="chemlab-guided-marker" aria-hidden="true">
                {state === 'done' ? '✓' : i + 1}
              </span>
              <div className="chemlab-guided-body">
                <p className="chemlab-guided-instruction">{step.instruction}</p>

                {state === 'current' && correction && (
                  <p className="chemlab-guided-correction" role="alert">{correction}</p>
                )}

                {/* What to look for, once there is something to look at. Shown
                    on the current step as a prompt and kept on finished ones,
                    because it is the sentence that says what the step MEANT. */}
                {step.observe && state !== 'upcoming' && (
                  <p className="chemlab-guided-observe">{step.observe}</p>
                )}

                {result && (
                  <StepResult
                    step={result}
                    /* Same rule as the free-form log: the newest result is the
                       one open, and opening another closes it. */
                    expanded={openId === undefined ? i === lastResultIndex : openId === result.id}
                    onToggle={() => setOpenId(id => (id === result.id ? '' : result.id))}
                  />
                )}

                {state === 'current' && isSelfAdvancing(step) && !loading && (
                  <button className="chemlab-btn chemlab-guided-continue" onClick={onContinue}>
                    Done — next step
                  </button>
                )}

                {/* The same advance as Continue, earned rather than clicked. */}
                {state === 'current' && step.expect.kind === 'answer-quantity' && !loading && (
                  <QuantityAsk step={step} working={working} onCorrect={onContinue} />
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {loading && <p className="chemlab-step-pending">reacting…</p>}
      {error && <p className="chemlab-error">{error}</p>}

      {finished && (
        <div className="chemlab-guided-conclusion">
          {experiment.conclusion && (
            <>
              <p className="chemlab-guided-eyebrow">What it shows</p>
              <p>{experiment.conclusion}</p>
            </>
          )}
          <button className="chemlab-btn chemlab-guided-continue" onClick={onRestart}>
            Run it again
          </button>
        </div>
      )}
    </div>
  );
}
